// COPIED WHOLESALE from image-os-web src/lib/qr.ts (the migration=copy rule; source named
// per the port doctrine). Client-safe pure helpers shared by both hubs — the Pro Hub reads
// and writes the SAME qr_code rows the Owner Hub's QR Studio manages, so the normalization
// must be byte-identical or the two surfaces would accept different links for one code.
// Seams: NONE — the file is dependency-free.
// THE DYNAMIC QR — the client-safe half: types and pure helpers.
//
// Kept out of qr.server.ts deliberately. A `.server` module is stripped from the client bundle, so
// anything the editor screen needs has to live here — the lesson the quick-replies build paid for.

/** What a code is for. 'pro' follows a person; 'studio' is the owner's own, named by its job. */
export type QrSubject = 'pro' | 'studio';

/**
 * Turn what someone typed into a destination we are willing to print behind.
 *
 * Peter has ruled that a pro chooses where their own code lands, and this does not narrow that:
 * every real web address passes through untouched. What it does is refuse the handful of inputs
 * that would make a public redirect on our domain into a weapon — `javascript:` and `data:` URLs,
 * and a link back into /q/ that would bounce forever. A redirector that forwards to anything at
 * all is how a domain ends up on a phishing blocklist, and the whole studio's email goes with it.
 *
 * It also does the kind thing with an Instagram handle, because that is what almost every pro
 * actually has: `@jane`, `jane`, or `instagram.com/jane` all become the same real link. Nobody
 * should have to know that a handle is not a URL.
 */
export function normalizeQrDestination(raw: string): string {
  const v = (raw ?? '').trim();
  if (!v) return '';

  // A bare handle — with or without the @ — is the common case and never looks like a URL.
  if (/^@[A-Za-z0-9._]{1,30}$/.test(v)) return `https://instagram.com/${v.slice(1)}`;

  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`;
  let u: URL;
  try { u = new URL(withScheme); } catch { return ''; }

  if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
  if (!u.hostname.includes('.')) {
    // "jane" parsed as a hostname — it was a handle all along.
    return /^[A-Za-z0-9._]{1,30}$/.test(v) ? `https://instagram.com/${v}` : '';
  }
  // No pointing a code at the redirector: /q/a -> /q/b -> /q/a is a loop with our name on it.
  if (/(^|\.)imageos\.co$/i.test(u.hostname) && u.pathname.startsWith('/q/')) return '';

  u.protocol = 'https:';
  return u.toString();
}

/** How the code is drawn on the sheet — the owner's choices. */
export const QR_COLORS = [
  { id: 'ink', label: 'Black' },
  { id: 'pink', label: 'Pink' },
] as const;
export const QR_SHAPES = [
  { id: 'square', label: 'Square' },
  { id: 'round', label: 'Round' },
  { id: 'disc', label: 'Circle' },
] as const;

/**
 * THE STYLE TILES (IOSW-262). Peter, holding Canva's panel up against ours: "the one you are
 * showing has too many buttons. Need something clean like this. You should have style options."
 * So the labeled colour/shape button rows are gone and the choice is a row of drawn codes — you
 * pick the one that looks right, exactly like Canva's template strip. Each tile is one
 * (shape, colour) pair rendered by the same generator that prints.
 */
export const QR_STYLES = [
  { id: 'square-ink', shape: 'square', color: 'ink', label: 'Classic black' },
  { id: 'round-ink', shape: 'round', color: 'ink', label: 'Dots black' },
  { id: 'frame-ink', shape: 'frame', color: 'ink', label: 'Framed black' },
  { id: 'disc-ink', shape: 'disc', color: 'ink', label: 'Circle black' },
  { id: 'burst-ink', shape: 'burst', color: 'ink', label: 'Burst black' },
  { id: 'badge-ink', shape: 'badge', color: 'ink', label: 'Badge black' },
  { id: 'square-pink', shape: 'square', color: 'pink', label: 'Classic pink' },
  { id: 'round-pink', shape: 'round', color: 'pink', label: 'Dots pink' },
  { id: 'frame-pink', shape: 'frame', color: 'pink', label: 'Framed pink' },
  { id: 'disc-pink', shape: 'disc', color: 'pink', label: 'Circle pink' },
  { id: 'burst-pink', shape: 'burst', color: 'pink', label: 'Burst pink' },
  { id: 'badge-pink', shape: 'badge', color: 'pink', label: 'Badge pink' },
] as const;
